import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  FormControl,
  Grid,
  MenuItem,
} from '@material-ui/core';

import { Check, Close } from '@material-ui/icons';

import { PartnerPartnership } from 'src/models';

import { useCommonStyles } from 'src/styles';

import {
  CustomDialogTitle,
  SelectControl,
  SpinnerWithProps,
} from 'src/components';

import { useStyles } from './styles';

import { Link } from '../../../../RegistrationLinks/RegistrationLinksModel';

import { getClientShortCodes } from '../../LinkStatisticsService';

import { PeriodType } from '../../LinkStatisticsModel';

import { useApiError } from '../../../../../providers';

export interface Props {
  open: boolean;
  translation: any;
  partnership?: string;
  partnerships: PartnerPartnership[];
  partnershipLinks: Link[];
  onClose: () => void;
  applyFilter: (
    partnership: string | undefined,
    selectedPeriod: number,
    selectedLink: Link | undefined,
  ) => void;
}

export const FiltersDialog: FC<Props> = ({
  open,
  partnership,
  partnerships,
  partnershipLinks,
  translation,
  onClose,
  applyFilter,
}) => {
  const classes = useStyles();

  const commonClasses = useCommonStyles();

  const { addError } = useApiError();

  const [loading, setLoading] = useState<boolean>(false);

  const [links, setLinks] = useState<Link[]>([]);

  const [selectedPartnership, setSelectedPartnership] =
    useState<string>();

  const [selectedPeriodType, setSelectedPeriodType] =
    useState<number>(PeriodType.Daily);

  const [selectedLink, setSelectedLink] = useState<Link>();

  const applyNewFilter = () => {
    applyFilter(
      selectedPartnership === 'All'
        ? undefined
        : selectedPartnership,
      selectedPeriodType,
      selectedLink,
    );
  };

  const clearFilter = () => {
    setSelectedPartnership('All');

    setSelectedPeriodType(PeriodType.Daily);

    setSelectedLink(undefined);

    setLinks([]);

    applyFilter(
      undefined,
      PeriodType.Daily,
      undefined,
    );
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (partnership) {
      setSelectedPartnership(partnership);

      setLinks(partnershipLinks);
    } else {
      setSelectedPartnership('All');

      setSelectedLink(undefined);

      setLinks([]);
    }
  }, [partnership, partnershipLinks]);

  const getLinksData = useCallback(
    async (partnershipId: number) => {
      setLoading(true);

      setLinks([]);

      setSelectedLink(undefined);

      try {
        const response = await getClientShortCodes(
          partnershipId,
        );

        const shortCodes = response.data?.shortCodes || [];

        const linksArr: Link[] = shortCodes.map(
          (shortCode: string) =>
            ({
              shortCode,
              link: shortCode,
            } as Link),
        );

        setLinks(linksArr);
      } catch (error: any) {
        setLinks([]);

        setSelectedLink(undefined);

        addError(
          JSON.stringify(error.response || error),
        );
      } finally {
        setLoading(false);
      }
    },
    [addError],
  );

  const handlePartnershipChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      const { value } = event.target;

      if (value === 'All') {
        setSelectedPartnership('All');

        setLinks([]);

        setSelectedLink(undefined);

        return;
      }

      const partnership = partnerships?.find(
        (p) => p.partnershipCode === value,
      );

      if (partnership) {
        setSelectedPartnership(
          partnership.partnershipCode,
        );

        setSelectedLink(undefined);

        await getLinksData(partnership.id);
      }
    },
    [partnerships, getLinksData],
  );

  const handlePeriodChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      setSelectedPeriodType(event.target.value);
    },
    [],
  );

  const handleLinkChange = useCallback(
    async (event: ChangeEvent<{ value: any }>) => {
      const link = links?.find(
        (l) => l.shortCode === event.target.value,
      );

      if (link) {
        setSelectedLink(link);
      } else {
        setSelectedLink(undefined);
      }
    },
    [links],
  );

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      classes={{ paper: classes.dialog }}
    >
      <CustomDialogTitle
        title={translation.filters}
        onClose={() => onClose()}
      />

      <SpinnerWithProps isLoading={loading} />

      <Grid container direction="column">
        <Grid item>
          <FormControl
            variant="outlined"
            className={commonClasses.formControl}
          >
            <p className={commonClasses.formControlTitle}>
              {translation.period_type}
            </p>

            <SelectControl
              value={selectedPeriodType}
              onChange={handlePeriodChange}
              minWidth={97}
            >
              <MenuItem
                className={commonClasses.optionSelector}
                style={{ width: 'inherit' }}
                value={PeriodType.Weekly}
                key={PeriodType.Weekly}
              >
                {translation.weekly}
              </MenuItem>

              <MenuItem
                className={commonClasses.optionSelector}
                style={{ width: 'inherit' }}
                key={PeriodType.Daily}
                value={PeriodType.Daily}
              >
                {translation.text_6421}
              </MenuItem>
            </SelectControl>
          </FormControl>
        </Grid>

        {partnerships &&
          partnerships.length !== 0 && (
            <Grid item>
              <FormControl
                variant="outlined"
                className={commonClasses.formControl}
              >
                <p
                  className={
                    commonClasses.formControlTitle
                  }
                >
                  {translation.text_6275}
                </p>

                <SelectControl
                  value={selectedPartnership}
                  onChange={handlePartnershipChange}
                  minWidth={97}
                >
                  <MenuItem
                    key="show_all"
                    className={
                      commonClasses.optionSelector
                    }
                    style={{ width: 'inherit' }}
                    value="All"
                  >
                    {translation.show_all}
                  </MenuItem>

                  {partnerships.map((partnership) => (
                    <MenuItem
                      className={
                        commonClasses.optionSelector
                      }
                      style={{ width: 'inherit' }}
                      key={partnership.id}
                      value={
                        partnership.partnershipCode
                      }
                    >
                      {partnership.partnershipCode}
                    </MenuItem>
                  ))}
                </SelectControl>
              </FormControl>
            </Grid>
          )}

        <Grid item>
          {selectedPartnership &&
            selectedPartnership !== 'All' && (
              <FormControl
                variant="outlined"
                className={commonClasses.formControl}
              >
                <p
                  className={
                    commonClasses.formControlTitle
                  }
                >
                  {translation.links}
                </p>

                <SelectControl
                  value={
                    selectedLink?.shortCode ?? 'All'
                  }
                  onChange={handleLinkChange}
                  minWidth={97}
                >
                  <MenuItem
                    key="show_all"
                    className={
                      commonClasses.optionSelector
                    }
                    style={{ width: 'inherit' }}
                    value="All"
                  >
                    {translation.show_all}
                  </MenuItem>

                  {links.map((link) => (
                    <MenuItem
                      className={
                        commonClasses.optionSelector
                      }
                      style={{ width: 'inherit' }}
                      key={link.shortCode}
                      value={link.shortCode}
                    >
                      {link.shortCode}
                    </MenuItem>
                  ))}
                </SelectControl>
              </FormControl>
            )}
        </Grid>
      </Grid>

      <DialogActions
        className={commonClasses.dialogAction}
      >
        <Button
          onClick={applyNewFilter}
          fullWidth
          startIcon={<Check />}
          className={commonClasses.activeDialogBtn}
        >
          {translation.text_65}
        </Button>

        <Button
          onClick={clearFilter}
          fullWidth
          startIcon={<Close />}
          className={commonClasses.cancelDialogBtn}
        >
          {translation.clearAll}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
